import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import Chip from 'tio-ui/components/chip';
import Text from 'tio-ui/components/text';
import { t } from 'ember-intl';
import MatchReason from './match-reason.gts';
import safeFormatDate from '../../helpers/safe-format-date.ts';

/** One employment line on a card. A person may hold several (tio-admin) or exactly one (tio-employee). */
export interface PersonCardEmployment {
  companyName?: string;
  payrollId?: string;
  email?: string;
  /** ISO instant; rendered as a "member since" date when present. */
  createdAt?: string;
}

/**
 * The card's view model. Callers normalize their own search-hit shape into this
 * and pass display-ready strings — the card neither reads the search index nor
 * decides which roles a viewer may see.
 */
export interface PersonCardPerson {
  /** Whatever `@route` expects as its model — a person id or an employee id. */
  id: string;
  firstName: string;
  lastName: string;
  isActiveEmployee: boolean;
  /** Has ever registered a login (may now be inactive). */
  isEnrolled: boolean;
  /** Has an active login. */
  isRegistered: boolean;
  /** Already-translated benefit participation status; omit on surfaces that don't show one. */
  participationLabel?: string;
  /** Already-translated and de-duplicated role labels. */
  roleLabels: string[];
  /** OpenSearch `matched_queries` names, for the match-reason chips and the exact-match border. */
  matchedQueries?: string[];
  employments: PersonCardEmployment[];
}

interface S {
  Args: {
    /** Display-ready view model — the card resolves no translations of its own
     * beyond the fixed status and enrollment labels. */
    person: PersonCardPerson;
    /** Detail route for the name link; receives `person.id` as its model. */
    route: string;
  };
  Element: HTMLDivElement;
}

/** Clause names that mean the hit matched a field exactly rather than fuzzily. */
const EXACT_MATCH_CLAUSES = ['name_exact', 'email', 'payroll', 'employee_id'];

/**
 * Full-width person summary card. Left column: identity (name + status chips +
 * match reason) and employment meta. Right side: role chips.
 *
 * Rendered as a div (not <Card>) so the border color can be set with a single
 * semantic class — Card's `tio-border-default` is defined later in the CSS and
 * would otherwise win.
 *
 * Expects the host app to define `people.status_active`, `people.status_inactive`,
 * `people.enrollment_active_enrolled`, `people.enrollment_enrolled_inactive`,
 * `people.enrollment_never` and `people.member_since`.
 */
export default class PersonCard extends Component<S> {
  /** Exact matches get a thicker green border; partial matches keep the default. */
  get borderClasses(): string {
    const set = new Set(this.args.person.matchedQueries ?? []);
    return EXACT_MATCH_CLAUSES.some((clause) => set.has(clause))
      ? 'border-2 tio-border-success'
      : 'border tio-border-default';
  }

  // The most specific (mutually exclusive) login state: active login >
  // enrolled-but-login-inactive > never enrolled. Distinct from the inclusive
  // "Ever enrolled" facet bucket, which also counts the active-enrolled people.
  get enrollmentLabelKey(): string {
    if (this.args.person.isRegistered) return 'people.enrollment_active_enrolled';
    if (this.args.person.isEnrolled) return 'people.enrollment_enrolled_inactive';
    return 'people.enrollment_never';
  }

  get enrollmentIntent(): 'primary' | 'warning' | 'default' {
    if (this.args.person.isRegistered) return 'primary';
    if (this.args.person.isEnrolled) return 'warning';
    return 'default';
  }

  <template>
    <div class="tio-bg-surface rounded-xl {{this.borderClasses}} p-5" ...attributes>
      <div class="tio-row-start-between flex-wrap gap-x-4 gap-y-2">
        {{! Left — identity + meta }}
        <div class="tio-fill-min tio-col gap-2">
          <div class="tio-cluster">
            <LinkTo
              @route={{@route}}
              @model={{@person.id}}
              class="font-medium text-link hover:text-link-hover"
            >
              {{@person.firstName}}
              {{@person.lastName}}
            </LinkTo>
            <Chip @intent={{if @person.isActiveEmployee "success" "danger"}} @size="sm">
              {{t (if @person.isActiveEmployee "people.status_active" "people.status_inactive")}}
            </Chip>
            {{#if @person.participationLabel}}
              <Chip @appearance="faded" @size="sm">{{@person.participationLabel}}</Chip>
            {{/if}}
            <Chip @intent={{this.enrollmentIntent}} @appearance="faded" @size="sm">
              {{t this.enrollmentLabelKey}}
            </Chip>
            {{#if @person.matchedQueries}}
              <MatchReason @matches={{@person.matchedQueries}} />
            {{/if}}
          </div>

          <div class="tio-cluster">
            {{#each @person.employments as |employment|}}
              {{#if employment.companyName}}
                <Text @size="sm" class="font-medium">{{employment.companyName}}</Text>
              {{/if}}
              {{#if employment.payrollId}}
                <Text @variant="muted" @size="sm">{{employment.payrollId}}</Text>
              {{/if}}
              {{#if employment.email}}
                <Text @variant="muted" @size="sm" class="break-all">{{employment.email}}</Text>
              {{/if}}
              {{#if employment.createdAt}}
                <Text @variant="muted" @size="sm">
                  {{t "people.member_since"}}
                  {{safeFormatDate employment.createdAt month="short" day="2-digit" year="numeric"}}
                </Text>
              {{/if}}
            {{/each}}
          </div>
        </div>

        {{! Right — role chips }}
        {{#if @person.roleLabels.length}}
          <div class="tio-cluster justify-end">
            {{#each @person.roleLabels as |role|}}
              <Chip @appearance="outlined" @size="sm">{{role}}</Chip>
            {{/each}}
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
